/**
 * Centralizes shared dialog, snackbar, spinner, and request-error UI state.
 */
import { createSlice } from '@reduxjs/toolkit';
import { cryptoRand } from '../../helpers/common';

export const initialState = {
  // Master spinner controls global loading feedback across async flows.
  masterSpinner: false,
  requestError: false,
  bookmarksCount_posearch_Spinner: false,
  bookmarksCount_collabreport_Spinner: false,
  getConversationCodesSpinner: false,
  fetchGroupedPONumbersSpinner: false,
  conversationSearchSpinner: false,
  lookupApiSpinner: false,
  defaultStakeHolderSpinner: false,
  executeBookmarkSpinner: false,
  errorCodeText: {
    errCode: '',
    errDesc: '',
    errTitle: ''
  },
  saveCollabReportSpinner: false,
  reportRequestError: false,
  // Snackbar queue stores generated ids so individual messages can be dismissed.
  snackbarContent: [],
  // Tracks the active cancellable request metadata used by request cancellation UI.
  isRequestCancellable: null,
  paginationSpinner: {
    isFetchingNextData: false,
    isFetchingPrevData: false
  },
  uploadingSpinner: false,
  getPMOVendorCodesSpinner: false,
  sessionTimeout: false
};

const dialogStatesSlice = createSlice({
  name: 'dialogStates',
  initialState,
  reducers: {
    /**
     * Toggles the global master spinner state.
     */
    toggleSpinner(state, action) {
      // TOGGLE_SPINNERS
      return { ...state, masterSpinner: action.payload };
    },
    /**
     * Removes a snackbar message from the queue by its generated id.
     */
    snackbarHandler(state, action) {
      // DELETE_SNACKBAR_CONTENT
      return {
        ...state,
        snackbarContent: state.snackbarContent.filter(
          (snackbarContent) => snackbarContent.id !== action.payload
        )
      };
    },
    /**
     * Clears all snackbar messages from UI state.
     */
    resetSnackbar(state) {
      // RESET_SNACKBAR_CONTENT
      return {
        ...state,
        snackbarContent: []
      };
    },
    /**
     * Merges arbitrary dialog-state flags into the current UI state.
     */
    updateDialogStates(state, action) {
      // UPDATE_DIALOG_STATES
      return { ...state, ...action.payload };
    },
    /**
     * Updates request error code, description, and title text.
     */
    updateErrorText(state, action) {
      // SET_REQUEST_ERROR_CODE_TEXT
      return {
        ...state,
        errorCodeText: {
          ...state.errorCodeText,
          ...action.payload
        }
      };
    },
    /**
     * Marks the session as timed out and stores the timeout error text.
     */
    setSessionTimeout(state, action) {
      // SET_SESSION_TIMEOUT
      console.log('setSessionTimeout', action.payload);
      return {
        ...state,
        sessionTimeout: true,
        errorCodeText: {
          ...state.errorCodeText,
          ...action.payload
        }
      };
    },
    /**
     * Appends a snackbar message with a generated id for later removal.
     */
    pushSnackbarContent(state, action) {
      // PUSH_SNACKBAR_CONTENT
      return {
        ...state,
        snackbarContent: [
          ...state.snackbarContent,
          { id: cryptoRand().toString().slice(2, 12), content: action.payload }
        ]
      };
    },
    /**
     * Exposes a cancellation action for sagas or middleware without changing state directly.
     */
    cancelRequest() {},
    /**
     * Updates next/previous table pagination loading flags.
     */
    togglePaginationSpinner(state, action) {
      // TABLE_PAGINATION_SPINNER
      return { ...state, paginationSpinner: { ...state.paginationSpinner, ...action.payload } };
    },
    /**
     * Stores request cancellation metadata for cancellable API calls.
     */
    setCancellableSpinner(state, action) {
      // SET_CANCELLABLE_SPINNER
      return { ...state, isRequestCancellable: action.payload };
    },
    /**
     * Sets the generic request-error flag and resets displayed error text.
     */
    setRequestError(state, action) {
      // SET_REQUEST_ERROR
      return { ...state, requestError: action.payload, errorCodeText: initialState.errorCodeText };
    },
    /**
     * Toggles the file-upload spinner state.
     */
    fileUploadEvents(state, action) {
      return { ...state, uploadingSpinner: action.payload };
    }
  }
});

export const {
  toggleSpinner,
  snackbarHandler,
  resetSnackbar,
  updateDialogStates,
  updateErrorText,
  setSessionTimeout,
  pushSnackbarContent,
  cancelRequest,
  togglePaginationSpinner,
  setCancellableSpinner,
  setRequestError,
  fileUploadEvents
} = dialogStatesSlice.actions;
export const dialogStatesActions = { ...dialogStatesSlice.actions };
export default dialogStatesSlice.reducer;
